import type { Track } from '@qlk/shared';
import { CONTENT_ID_LABEL } from '@qlk/shared';
import { Music2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { coQuyen, useDuongDanTrang, useToi } from '../../../lib/auth.js';
import {
  DataTable,
  EmptyState,
  KhoiMuc,
  Nut,
  OChon,
  OTimKiem,
  PageHeader,
  SegmentedControl,
  StatStrip,
  StatusBadge,
  ToolbarBang,
  type CotBang,
  type OChiSo,
} from '../../../ui/index.js';
import {
  useDanhSachBai,
  useNguonNhac,
  useNhapCsv,
  useSuDungGanDay,
  useTaoBaiNhac,
  useThongKeNhac,
  useTomTatNhac,
} from '../api.js';
import { doDai, nhanGiayPhep, TONE_CONTENT_ID, TONE_NGUON } from '../components/dinhDang.js';
import { FormBaiNhac } from '../components/FormBaiNhac.js';
import { FormNhapCsv } from '../components/FormNhapCsv.js';
import { KhungWhitelist } from '../components/KhungWhitelist.js';
import { NutNgheThu } from '../components/NutNgheThu.js';
import { SuDungGanDay } from '../components/SuDungGanDay.js';
import { TheNguonNhac } from '../components/TheNguonNhac.js';

type TabId = 'tat-ca' | 'own' | 'hop-tac';

const KHOANG_BPM: { value: string; label: string; min?: number; max?: number }[] = [
  { value: '', label: 'Mọi BPM' },
  { value: 'cham', label: 'Dưới 70', max: 69 },
  { value: 'vua', label: '70 – 100', min: 70, max: 100 },
  { value: 'nhanh', label: 'Trên 100', min: 101 },
];

export function TrangKhoNhac() {
  const duongDan = useDuongDanTrang('NỘI DUNG');
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'music', 'edit');

  const [tab, setTab] = useState<TabId>('tat-ca');
  const [q, setQ] = useState('');
  const [mood, setMood] = useState('');
  const [bpm, setBpm] = useState('');
  const [bangMo, setBangMo] = useState<'khong' | 'them' | 'csv'>('khong');

  const khoang = KHOANG_BPM.find((k) => k.value === bpm);
  const loc = {
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(tab === 'own' ? { sourceKind: 'own' } : {}),
    ...(mood ? { mood } : {}),
    ...(khoang?.min === undefined ? {} : { bpmMin: khoang.min }),
    ...(khoang?.max === undefined ? {} : { bpmMax: khoang.max }),
  };

  const danhSach = useDanhSachBai(loc);
  const tomTat = useTomTatNhac();
  const thongKe = useThongKeNhac();
  const nguon = useNguonNhac();
  const suDung = useSuDungGanDay();
  const taoBai = useTaoBaiNhac();
  const nhapCsv = useNhapCsv();

  const tatCaDong = useMemo(
    () => danhSach.data?.pages.flatMap((t) => t.items) ?? [],
    [danhSach.data?.pages],
  );

  // Tab "Kho hợp tác" gộp nhạc đối tác và nhạc kho Network nên lọc ở phía giao
  // diện; hai tab còn lại lọc thẳng trên máy chủ.
  const dong = tab === 'hop-tac' ? tatCaDong.filter((d) => d.sourceKind !== 'own') : tatCaDong;

  const moods = useMemo(() => {
    const tap = new Set(tatCaDong.flatMap((d) => d.moods));
    return [
      { value: '', label: 'Mọi tâm trạng' },
      ...[...tap].sort().map((m) => ({ value: m, label: m })),
    ];
  }, [tatCaDong]);

  /** Bài Miss Records đã đăng ký Content ID, dùng cho khung whitelist. */
  const baiNoiBo = useMemo(
    () => tatCaDong.find((d) => d.sourceKind === 'own' && d.contentIdStatus === 'registered'),
    [tatCaDong],
  );

  const cot: CotBang<Track>[] = [
    {
      key: 'bai',
      header: 'Bài nhạc',
      width: '280px',
      render: (d) => (
        <div className="flex items-center gap-3">
          <NutNgheThu title={d.title} previewUrl={d.previewUrl} />
          <div className="min-w-0">
            <Link to={`/kho-nhac/${d.id}`} className="font-semibold text-chu hover:text-nhan">
              {d.title}
            </Link>
            <p className="truncate text-phu text-chu-mo uppercase">{d.artist}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'nguon',
      header: 'Nguồn',
      render: (d) => (
        <StatusBadge tone={TONE_NGUON[d.sourceKind]} cham={false}>
          {d.sourceName}
        </StatusBadge>
      ),
    },
    {
      key: 'mood',
      header: 'Thể loại · Mood',
      render: (d) => (
        <span className="text-chu-phu">
          {[d.genre, d.moods.join(', ')].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      key: 'bpm',
      header: 'BPM',
      align: 'right',
      render: (d) => <span className="so-lieu">{d.bpm ?? '—'}</span>,
    },
    {
      key: 'dai',
      header: 'Dài',
      align: 'right',
      render: (d) => <span className="so-lieu">{doDai(d.durationSec)}</span>,
    },
    {
      key: 'contentId',
      header: 'Content ID',
      render: (d) => (
        <StatusBadge tone={TONE_CONTENT_ID[d.contentIdStatus]} cham={false}>
          {CONTENT_ID_LABEL[d.contentIdStatus]}
        </StatusBadge>
      ),
    },
    {
      key: 'whitelist',
      header: 'Whitelist',
      align: 'right',
      render: (d) =>
        d.contentIdStatus === 'registered' ? (
          <span
            className={`so-lieu font-semibold ${d.whitelistedCount < d.channelCount ? 'text-warn-chu' : 'text-ok-chu'}`}
          >
            {d.whitelistedCount}/{d.channelCount}
          </span>
        ) : (
          <span className="text-chu-mo">—</span>
        ),
    },
    {
      key: 'giayPhep',
      header: 'Giấy phép',
      render: (d) => {
        const { text, mau } = nhanGiayPhep(d);
        return <span className={`font-medium ${mau}`}>{text}</span>;
      },
    },
    {
      key: 'daDung',
      header: 'Đã dùng',
      align: 'right',
      render: (d) => <span className="so-lieu">{d.usageCount}</span>,
    },
  ];

  const s = tomTat.data;
  const tk = thongKe.data;

  /*
   * Bốn `StatCard` rời thành **một** `StatStrip`. Dòng phụ của mỗi ô giữ
   * nguyên con số nó vốn nói — đó là chỗ duy nhất trang này ghi "bao nhiêu bài
   * đã đăng ký Content ID" hay "hết hạn trong 30 ngày tới".
   */
  const chiSo: OChiSo[] = [
    {
      nhan: 'Nhạc Miss Records · hiện tại',
      giaTri: s ? s.ownTracks.toLocaleString('vi-VN') : '—',
      ...(s ? { phu: `${String(s.registeredContentId)} bài đã đăng ký Content ID` } : {}),
    },
    {
      nhan: 'Nhạc kho hợp tác · hiện tại',
      giaTri: s ? s.partnerTracks.toLocaleString('vi-VN') : '—',
      ...(tk ? { phu: `Từ ${String(tk.partnerSourceCount)} nguồn có giấy phép` } : {}),
    },
    {
      nhan: 'Lượt dùng · tháng này',
      giaTri: tk ? tk.usageThisMonth.toLocaleString('vi-VN') : '—',
      ...(tk
        ? {
            phu: `${String(tk.ownUsagePercent)}% nhạc Miss Records · ${String(tk.partnerUsagePercent)}% hợp tác`,
          }
        : {}),
    },
    {
      nhan: 'Giấy phép sắp hết hạn · 30 ngày tới',
      giaTri: s ? (
        <span className={s.expiringSoon > 0 ? 'text-warn-chu' : undefined}>{s.expiringSoon}</span>
      ) : (
        '—'
      ),
      ...(tk
        ? { phu: `Trong 30 ngày tới · đang dùng ở ${String(tk.expiringVideoCount)} video` }
        : {}),
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Kho nhạc"
        phu={
          s
            ? `${s.totalTracks.toLocaleString('vi-VN')} bài · ${s.ownTracks.toLocaleString('vi-VN')} của Miss Records`
            : undefined
        }
        giaiThich="Nhạc do Miss Records sản xuất và nhạc từ kho hợp tác — giấy phép, Content ID và lịch sử sử dụng."
        actions={
          coSua && (
            <>
              <Nut
                kieu="phu"
                onClick={() => {
                  setBangMo((cu) => (cu === 'csv' ? 'khong' : 'csv'));
                }}
              >
                Nhập danh mục CSV
              </Nut>
              <Nut
                kieu="chinh"
                onClick={() => {
                  setBangMo((cu) => (cu === 'them' ? 'khong' : 'them'));
                }}
              >
                + Thêm bài nhạc
              </Nut>
            </>
          )
        }
      />

      <div className="mb-5">
        <StatStrip aria-label="Chỉ số kho nhạc" o={chiSo} />
      </div>

      {bangMo === 'them' && coSua && (
        <div className="mb-5">
          <KhoiMuc tieuDe="Thêm bài nhạc">
            <FormBaiNhac
              nguon={nguon.data ?? []}
              dangGui={taoBai.isPending}
              loi={taoBai.error?.message ?? null}
              onHuy={() => {
                setBangMo('khong');
              }}
              onGui={(input) => {
                taoBai.mutate(input, {
                  onSuccess: () => {
                    setBangMo('khong');
                  },
                });
              }}
            />
          </KhoiMuc>
        </div>
      )}

      {bangMo === 'csv' && coSua && (
        <div className="mb-5">
          <KhoiMuc tieuDe="Nhập danh mục từ CSV">
            <FormNhapCsv
              nguon={nguon.data ?? []}
              dangGui={nhapCsv.isPending}
              loi={nhapCsv.error?.message ?? null}
              ketQua={nhapCsv.data ?? null}
              onHuy={() => {
                setBangMo('khong');
                nhapCsv.reset();
              }}
              onGui={(input) => {
                nhapCsv.mutate({ ...input, capNhatNeuTrung: true });
              }}
            />
          </KhoiMuc>
        </div>
      )}

      {/*
        `SegmentedControl`, không `Tabs`: ba mục này **lọc một bảng**, không đổi
        nội dung cả trang — hai thứ phải khác nhau về hình để người dùng biết
        bấm vào thì chuyện gì xảy ra (xem `docs/ui.md` mục 3).
      */}
      <ToolbarBang
        trai={
          <SegmentedControl
            aria-label="Lọc theo nguồn nhạc"
            giaTri={tab}
            onChange={(v) => {
              setTab(v as TabId);
            }}
            muc={[
              { giaTri: 'tat-ca', nhan: 'Tất cả', ...(s ? { dem: s.totalTracks } : {}) },
              { giaTri: 'own', nhan: 'Miss Records', ...(s ? { dem: s.ownTracks } : {}) },
              { giaTri: 'hop-tac', nhan: 'Kho hợp tác', ...(s ? { dem: s.partnerTracks } : {}) },
            ]}
          />
        }
        phai={
          <>
            <OTimKiem
              value={q}
              onChange={setQ}
              label="Tìm bài nhạc"
              placeholder="Tên bài, nghệ sĩ, ISRC…"
              className="max-w-[260px]"
            />
            <OChon label="Tâm trạng" value={mood} onChange={setMood} options={moods} />
            <OChon
              label="Khoảng BPM"
              value={bpm}
              onChange={setBpm}
              options={KHOANG_BPM.map((k) => ({ value: k.value, label: k.label }))}
            />
          </>
        }
      />

      {danhSach.error ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState loi title="Không tải được kho nhạc" description={danhSach.error.message} />
        </div>
      ) : (
        <DataTable
          caption="Danh sách bài nhạc"
          columns={cot}
          rows={dong}
          keyOf={(d) => d.id}
          dangTai={danhSach.isPending}
          conNua={danhSach.hasNextPage}
          dangTaiThem={danhSach.isFetchingNextPage}
          onTaiThem={() => void danhSach.fetchNextPage()}
          khiRong={
            <EmptyState
              icon={Music2}
              title="Không có bài nhạc nào khớp bộ lọc"
              description="Thử bỏ bớt điều kiện tìm kiếm, hoặc nhập thêm danh mục từ CSV."
            />
          }
        />
      )}

      <div className="mt-6 grid grid-cols-1 gap-6">
        <KhungWhitelist bai={baiNoiBo} coSua={coSua} />
        <TheNguonNhac nguon={nguon.data ?? []} dangTai={nguon.isPending} />
        <SuDungGanDay
          dong={suDung.data ?? []}
          dangTai={suDung.isPending}
          loi={suDung.error?.message ?? null}
        />
      </div>
    </>
  );
}
